import { Button } from "@/components/ui/button";
import { Pencil, Trash2, GripVertical } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useCarriers, type Carrier } from "@/hooks/useCarriers";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface CarriersTableProps {
  carriers: Carrier[];
  onEdit: (carrier: Carrier) => void;
}

interface SortableRowProps {
  carrier: Carrier;
  onEdit: (carrier: Carrier) => void;
  onDelete: (id: string) => void;
}

const SortableRow = ({ carrier, onEdit, onDelete }: SortableRowProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: carrier.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 1 : 0,
    position: isDragging ? "relative" as const : "static" as const,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <TableRow ref={setNodeRef} style={style}>
      <TableCell>
        <Button
          variant="ghost"
          size="icon"
          className="cursor-grab"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" />
        </Button>
      </TableCell>
      <TableCell className="text-[14px]">{carrier.name}</TableCell>
      <TableCell className="text-[14px]">{carrier.description}</TableCell>
      <TableCell className="flex space-x-2">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onEdit(carrier)}
        >
          <Pencil className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onDelete(carrier.id)}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </TableCell>
    </TableRow>
  );
};

export const CarriersTable = ({ carriers, onEdit }: CarriersTableProps) => {
  const { deleteCarrier, updateOrder } = useCarriers();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;

    const oldIndex = carriers.findIndex((carrier) => carrier.id === active.id);
    const newIndex = carriers.findIndex((carrier) => carrier.id === over.id);

    const newCarriers = [...carriers];
    const [removed] = newCarriers.splice(oldIndex, 1);
    newCarriers.splice(newIndex, 0, removed);

    updateOrder.mutate(newCarriers);
  };

  const handleDelete = (id: string) => {
    deleteCarrier.mutate(id);
  };

  return (
    <div className="border rounded-lg">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[50px]"></TableHead>
            <TableHead>Name</TableHead>
            <TableHead>Description</TableHead>
            <TableHead className="w-[100px]">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={carriers.map((c) => c.id)}
              strategy={verticalListSortingStrategy}
            >
              {carriers?.map((carrier) => (
                <SortableRow
                  key={carrier.id}
                  carrier={carrier}
                  onEdit={onEdit}
                  onDelete={handleDelete}
                />
              ))}
            </SortableContext>
          </DndContext>
        </TableBody>
      </Table>
    </div>
  );
};